import { baseApi } from './baseApi';
import type { AuthUser } from '../../types';

interface LoginRequest { email: string; password: string }
interface LoginResponse { accessToken: string; user: AuthUser }
interface ImpersonateResponse { accessToken: string; user: AuthUser & { impersonatedBy?: string } }

export const authApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    login: build.mutation<LoginResponse, LoginRequest>({
      query: (body) => ({ url: '/auth/login', method: 'POST', body }),
      transformResponse: (res: { data: LoginResponse }) => res.data,
    }),
    demoStart: build.mutation<LoginResponse, void>({
      query: () => ({ url: '/demo/start', method: 'POST' }),
      transformResponse: (res: { data: LoginResponse }) => res.data,
    }),
    logout: build.mutation<void, void>({
      query: () => ({ url: '/auth/logout', method: 'POST' }),
    }),
    /** Refresh silencioso con la cookie httpOnly (no requiere accessToken en memoria).
     *  Se usa al arrancar la app para restaurar la sesión en deep-links en frío. */
    refresh: build.mutation<{ accessToken: string }, void>({
      query: () => ({ url: '/auth/refresh', method: 'POST' }),
      transformResponse: (res: { data: { accessToken: string } }) => res.data,
    }),
    getMe: build.query<AuthUser, void>({
      query: () => '/auth/me',
      transformResponse: (res: { data: AuthUser }) => res.data,
    }),
    impersonate: build.mutation<ImpersonateResponse, string>({
      query: (userId) => ({ url: `/auth/impersonate/${userId}`, method: 'POST' }),
      transformResponse: (res: { data: ImpersonateResponse }) => res.data,
    }),
    stopImpersonation: build.mutation<LoginResponse, void>({
      query: () => ({ url: '/auth/impersonate/stop', method: 'POST' }),
      transformResponse: (res: { data: LoginResponse }) => res.data,
    }),
    forgotPassword: build.mutation<{ message: string }, { email: string }>({
      query: (body) => ({ url: '/auth/forgot-password', method: 'POST', body }),
      transformResponse: (res: { data: { message: string } }) => res.data,
    }),
    resetPassword: build.mutation<void, { token: string; password: string }>({
      query: (body) => ({ url: '/auth/reset-password', method: 'POST', body }),
    }),
  }),
});

export const {
  useLoginMutation,
  useDemoStartMutation,
  useLogoutMutation,
  useRefreshMutation,
  useGetMeQuery,
  useImpersonateMutation,
  useStopImpersonationMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
} = authApi;
